/** @jsxImportSource @emotion/react */
import { useTranslate } from '@/i18n/useTranslate';
import React, { FC, useCallback, useState } from 'react';
import { PinCodeForm } from './fragments/PinCodeForm';
import { PasswordForm } from './fragments/PasswordForm';
import { BoxHeader, WhiteBox } from '@/components/WhiteBox';
import { useNavigate } from 'react-router-dom';
import { css } from '@mui/material';
import { Warning } from '@/components';
import { AppRoutes } from '@/routes/appRoutes';
import DATA_TEST_IDS from './dataTestIds';
import { testId } from '@/utils/testId';

export const Password: FC = () => {
  const { t } = useTranslate('profile');
  const navigate = useNavigate();
  const [step, setStep] = useState<'set-password' | 'send-pincode' | 'success'>(
    'set-password'
  );
  const [pinFailed, setPinFailed] = useState(false);
  const goToPin = useCallback(() => {
    setStep('send-pincode');
  }, []);

  const goTo = useCallback((failed?: boolean) => {
    if (failed) {
      setPinFailed(true);
      setStep('set-password');
    } else {
      setStep('success');
    }
  }, []);

  let form = null;

  switch (step) {
    case 'set-password':
      form = <PasswordForm next={goToPin} />;
      break;
    case 'success':
    case 'send-pincode':
      form = <PinCodeForm next={goTo} />;
      break;
  }

  return (
    <>
      <div
        css={css`
          height: 100%;
          display: flex;
          flex-direction: column;
          & > section {
            height: 100%;
          }
        `}
      >
        <WhiteBox>
          <BoxHeader sx={{ mb: { xs: 24, sm: 12 } }}>
            {t('CHANGE_PASSWORD')}
          </BoxHeader>
          {form}
        </WhiteBox>
      </div>
      <Warning
        data-test-id={testId(
          DATA_TEST_IDS.securityFragmentPassword + '_warning-pwd-been-changed'
        )}
        open={step === 'success'}
        title={t('SUCCESS')}
        type={'success'}
        onClose={() =>
          navigate({
            pathname: AppRoutes.PROFILE,
            search: window.location.search,
          })
        }
      >
        {t('CONGRATULATIONS_YOUR_PASSWORD_HAS_BEEN_CHANGED')}
      </Warning>
      <Warning
        data-test-id={testId(
          DATA_TEST_IDS.securityFragmentPassword + '_warning-error-wrong-pin'
        )}
        open={pinFailed}
        title={t('ERROR')}
        type={'error'}
        onClose={() => {
          setStep('set-password');
          setPinFailed(false);
        }}
      >
        {t('WRONG_PIN_ENTERED_PLEASE_TRY_AGAIN')}
      </Warning>
    </>
  );
};
